import {
  AtomIcon,
  BinocularsIcon,
  BlueprintIcon,
  BookOpenIcon,
  ChatsIcon,
  CrosshairIcon,
  CubeIcon,
  FireIcon,
  FlagIcon,
  GearIcon,
  GraduationCapIcon,
  HandshakeIcon,
  HeartIcon,
  HourglassIcon,
  LightbulbIcon,
  LightningIcon,
  MagnifyingGlassIcon,
  MapTrifoldIcon,
  MegaphoneIcon,
  PathIcon,
  QuestionIcon,
  RulerIcon,
  ScalesIcon,
  ShieldCheckIcon,
  SignpostIcon,
  SparkleIcon,
  StackIcon,
  StairsIcon,
  SwordIcon,
  TargetIcon,
  TreeStructureIcon,
  UserFocusIcon,
  WavesIcon,
  WrenchIcon,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

// Every archetype image, keyed by its filename slug — which is exactly the
// API's `archetype.name` lowercased and hyphenated ("the-storyteller").
const artwork = import.meta.glob<string>("../../../assets/dna/*.png", {
  eager: true,
  import: "default",
});

const toSlug = (archetypeName: string) =>
  archetypeName.trim().toLowerCase().replace(/\s+/g, "-");

/**
 * Artwork for an archetype name as the API returns it. Undefined for a name we
 * have no image for, so the card degrades instead of breaking when the backend
 * adds an archetype before we ship its assets.
 */
export const getDnaArtwork = (archetypeName: string): string | undefined =>
  artwork[`../../../assets/dna/${toSlug(archetypeName)}.png`];

export type DnaTrait = {
  /** Label key under pages.user.edit.creatorDna.traits. */
  key: string;
  icon: Icon;
};

/**
 * The three traits shown as chips under each archetype name, keyed by the same
 * slug as the artwork.
 */
const TRAITS_BY_SLUG: Record<string, DnaTrait[]> = {
  "the-architect": [
    { key: "structured", icon: StackIcon },
    { key: "foundationsFirst", icon: CubeIcon },
    { key: "blueprintLed", icon: BlueprintIcon },
  ],
  "the-provocateur": [
    { key: "highEnergy", icon: LightningIcon },
    { key: "provocative", icon: FireIcon },
    { key: "momentumDriven", icon: WavesIcon },
  ],
  "the-challenger": [
    { key: "challenging", icon: SwordIcon },
    { key: "twoSided", icon: ScalesIcon },
    { key: "dialectic", icon: ChatsIcon },
  ],
  "the-engineer": [
    { key: "systematic", icon: GearIcon },
    { key: "handsOn", icon: WrenchIcon },
    { key: "causeAndEffect", icon: TreeStructureIcon },
  ],
  "the-explorer": [
    { key: "curious", icon: MagnifyingGlassIcon },
    { key: "openEnded", icon: QuestionIcon },
    { key: "discoveryLed", icon: BinocularsIcon },
  ],
  "the-guide": [
    { key: "warmTone", icon: HeartIcon },
    { key: "stepByStep", icon: StairsIcon },
    { key: "reassuring", icon: ShieldCheckIcon },
  ],
  "the-iconoclast": [
    { key: "contrarian", icon: SignpostIcon },
    { key: "mythBusting", icon: LightbulbIcon },
    { key: "bold", icon: MegaphoneIcon },
  ],
  "the-logician": [
    { key: "rigorous", icon: RulerIcon },
    { key: "firstPrinciples", icon: AtomIcon },
    { key: "precise", icon: CrosshairIcon },
  ],
  "the-mentor": [
    { key: "personal", icon: UserFocusIcon },
    { key: "experienced", icon: GraduationCapIcon },
    { key: "encouraging", icon: HandshakeIcon },
  ],
  "the-storyteller": [
    { key: "warmTone", icon: HeartIcon },
    { key: "vividImagery", icon: SparkleIcon },
    { key: "storyFirst", icon: BookOpenIcon },
  ],
  "the-strategist": [
    { key: "goalFirst", icon: TargetIcon },
    { key: "bigPicture", icon: MapTrifoldIcon },
    { key: "decisive", icon: FlagIcon },
  ],
  "the-educator": [
    { key: "responsive", icon: PathIcon },
    { key: "patient", icon: HourglassIcon },
    { key: "oneToOne", icon: HandshakeIcon },
  ],
};

export const getDnaTraits = (archetypeName: string): DnaTrait[] =>
  TRAITS_BY_SLUG[toSlug(archetypeName)] ?? [];

/** Translation key holding the localized name / description for an archetype. */
export const getDnaCopyKey = (archetypeName: string, field: string) =>
  `pages.user.edit.creatorDna.archetypes.${toSlug(archetypeName)}.${field}`;
